import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function LoginPage() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const error = params.get("error");
  const [domains, setDomains] = useState([]);

  useEffect(() => {
    api.get("/auth/public-config").then((r) => setDomains(r.data.allowed_domains || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (error) {
      const messages = {
        state_mismatch: "Session expired, please try again.",
        token_exchange: "Could not exchange Google authorization code.",
        no_id_token: "Google did not return an ID token.",
        bad_id_token: "Invalid ID token from Google.",
        domain_forbidden: "Your Google email domain is not allowed. Ask the CEO to add your domain.",
      };
      toast.error(messages[error] || `Authentication error: ${error}`);
    }
  }, [error]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Real Google OAuth 2.0 flow handled by backend (/api/auth/google/login sets cookies + redirects to Google).
    // Use window.location.origin (runtime) so the flow works seamlessly on preview, prod, and custom domains.
    window.location.href = `${window.location.origin}/api/auth/google/login`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-white" data-testid="login-page">
      {/* Left panel */}
      <div className="lg:col-span-2 flex flex-col justify-between p-8 md:p-12 border-r border-zinc-200 relative overflow-hidden">
        <div className="flex items-center gap-3 relative z-10">
          <img src="/logo.png" alt="BAIM" className="w-10 h-10 object-contain" data-testid="app-logo-login" />
          <div className="font-heading font-bold tracking-tight text-lg">BAIM</div>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">Mission Control</div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight mb-6">
            Run every consulting engagement with precision.
          </h1>
          <p className="text-zinc-600 text-base leading-relaxed mb-10">
            Timesheets, staffing, budgets and validation — in one disciplined workspace used by your entire firm.
          </p>
          <button
            data-testid="google-sso-btn"
            onClick={handleLogin}
            className="group w-full md:w-auto inline-flex items-center justify-center gap-3 px-6 h-12 bg-zinc-950 text-white rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors btn-primary-shadow"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#fff" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#fff" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" opacity=".85"/>
              <path fill="#fff" d="M3.964 10.71A5.41 5.41 0 013.68 9c0-.593.102-1.17.283-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" opacity=".7"/>
              <path fill="#fff" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.164 6.656 3.58 9 3.58z" opacity=".55"/>
            </svg>
            Continue with Google
          </button>
          <p className="text-xs text-zinc-500 mt-4">
            Allowed domains:{" "}
            {domains.length > 0
              ? domains.map((d) => <span key={d} className="font-mono text-zinc-900 mr-2">@{d}</span>)
              : <span className="font-mono text-zinc-900">@ba-inmotion.com</span>}
          </p>
        </div>

        <div className="text-xs text-zinc-400 relative z-10 font-mono">
          v1.0 — © BAIM
        </div>
      </div>

      {/* Right panel */}
      <div className="hidden lg:flex lg:col-span-3 bg-zinc-950 text-white relative overflow-hidden flex-col justify-end p-12">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/6bc84ddb-c989-4cd0-8f7b-447196eb48dd/images/4acfae56bc0f09c7fc8f3e117f15661bece1f0985cffd577ec3514a16a39429b.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950 via-zinc-950/70 to-transparent" />

        <div className="relative max-w-lg">
          <div className="grid grid-cols-3 gap-3 mb-12">
            {["#2563EB", "#CA8A04", "#C2410C", "#4F46E5", "#059669", "#DB2777"].map((c, i) => (
              <div key={i} className="h-20 rounded-sm" style={{ background: c }} />
            ))}
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Q1 2026 · Mission Health</div>
          <h2 className="font-heading text-3xl font-semibold leading-tight mb-4">
            Where every hour you book is an hour you can account for.
          </h2>
          <p className="text-zinc-400 text-sm">
            Built for consulting firms that take budgets, staffing and billing seriously.
          </p>
        </div>
      </div>
    </div>
  );
}
